import type { DashboardItem, DashboardItemDataType, OldDashboardItem } from 'types';
import { getUUID } from './common';

export const formatDashboardData = <T>(data: T | null | undefined): DashboardItem[] => {
  if (!data) return [];

  let rawItems: DashboardItem[];

  if (Array.isArray(data)) {
    const isOldFormat = data.some((item) => ['info', 'activities', 'modules'].includes(item?.type));

    rawItems = isOldFormat ? transformOldDashboard(data as unknown as OldDashboardItem[]) : (data as unknown as DashboardItem[]);
  } else {
    rawItems = Object.entries(data as Record<string, unknown>).map(
      ([key, value]) =>
        ({
          ...(value as Record<string, unknown>),
          type: key,
        }) as DashboardItem
    );
  }

  let currentMaxRow = rawItems.reduce((max, item) => {
    return item?.row != null ? Math.max(max, item.row) : max;
  }, -1);

  return rawItems
    .map((item) => {
      let row = item?.row;

      if (row == null) {
        currentMaxRow += 1;
        row = currentMaxRow;
      }

      return {
        ...item,
        row,
        items: (item?.items ?? []).map((x) => ({
          ...x,
          id: x.id ?? getUUID(),
        })),
      } as DashboardItem;
    })
    .sort((a, b) => (a.row as number) - (b.row as number));
};

export const transformOldDashboard = (oldDashboard: OldDashboardItem[]): DashboardItem[] => {
  const mapToAgendaItems = (data: DashboardItemDataType[]) => {
    return data.map((item, index) => ({
      order: index + 1,
      borderColor: '',
      type: 'AgendaView',
      kpi: null,
      agendaView: {
        moduleId: item.moduleId,
        agendaId: item.agendaId,
        viewId: item.viewId,
      },
    }));
  };

  const mapToKpiItems = (data: DashboardItemDataType[]) => {
    return data.map((item, index) => ({
      order: index + 1,
      borderColor: '',
      type: 'KPI',
      agendaView: null,
      kpi: {
        link: null,
        view: {
          moduleId: item.moduleId,
          agendaId: item.agendaId,
          viewId: item.viewId,
        },
      },
    }));
  };

  return oldDashboard.map((item, index) => {
    const row = index + 1;

    if (item.type === 'activities') {
      return {
        type: 'agendaViewItems',
        row,
        items: mapToAgendaItems(item.data ?? []),
      } as unknown as DashboardItem;
    }

    if (item.type === 'info') {
      return {
        type: 'kpiItems',
        row,
        items: mapToKpiItems(item.data ?? []),
      } as unknown as DashboardItem;
    }

    if (item.type === 'modules') {
      return {
        type: 'modules',
        row,
        viewMode: 'viewMode' in item ? item.viewMode : 'module',
      } as DashboardItem;
    }

    return item as unknown as DashboardItem;
  });
};
